import { useTranslation } from 'react-i18next'

import { Checkbox, DatePicker, Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { useClientContacts } from '@/features/directories/api/useDirectory'
import { ClientSelect } from '@/features/directories/components/ClientSelect'
import { useQuoteClientContacts } from '@/features/quotes/api/useQuote'
import { soleContactId, toQuoteClientContacts } from '@/features/quotes/clientContacts'
import { CreditDaysField } from '@/features/quotes/components/CreditDaysField'
import {
  draftFromRecord,
  EMPTY_QUOTE_DRAFT,
  isBlankOrCreditDays,
  isCreditTerms,
  type QuoteDraft,
} from '@/features/quotes/quoteDraft'
import { creditDaysError, errorMessage } from '@/features/quotes/quoteText'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'
import type { QuoteClientContact, QuoteDetail } from '@/types/domain'

/* "New quote" — the entry to UJ-1.
 *
 * This sheet takes the HEADER only: client, validity, and the terms that are
 * agreed at the same moment. Cargo and cost lines are entered on the quote
 * itself, where the checklist can watch them and the totals can recalculate
 * live. A modal that tried to hold all three would be the Excel path with extra
 * steps, and quoting must not be slower than the Excel it replaces.
 *
 * The client is a DIRECTORY PICK rather than a typed name (FR-39). That is the
 * substitution the whole product exists to make: the source workbook joined its
 * sheets on a company name somebody retyped each time, and the same client
 * arrived as four spellings. Typing a name here would have rebuilt that.
 */

/* The draft shape, the validators and the empty header come from quoteDraft.ts,
 * which was extracted for exactly this reason: this sheet and step 1 of the
 * create form ask the same questions, and two copies of these predicates would
 * eventually disagree about whether "20 " is a number — one surface sending
 * what the other refused.
 *
 * The copies that used to live in this file were byte-identical to that module
 * and outlived the extraction. They are gone now: the participants list (FR-24)
 * was the first field to exist in one and not the other, and a second shape
 * would have made toQuotePayload silently drop the parties submitted here. */

/** A whole number, or blank. Empty is legitimate: transit time is optional. */
function isBlankOrCount(raw: string): boolean {
  const normalised = raw.trim()
  return normalised === '' || /^\d+$/.test(normalised)
}

/** A decimal measure, or blank. A comma is accepted because that is what a
 *  Russian keyboard produces, and the payload normalises it to a dot. */
function isBlankOrDecimal(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised === '' || /^\d+(\.\d+)?$/.test(normalised)
}

export function QuoteFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  failure,
}: {
  open: boolean
  onClose: () => void
  /** Absent for a new quote.
   *
   *  QuoteDetail rather than QuoteSummary, because the EDIT prefill needs
   *  `cargoTotals` — the list shape carries a rounder `cargo` summary instead,
   *  and prefilling the three measure overrides from a summary is not possible.
   *  Nothing is lost by narrowing it: the only surface that edits a header is
   *  the detail page, which holds the detail. */
  record?: QuoteDetail
  onSubmit: (values: QuoteDraft) => void
  pending?: boolean
  /** The save's refusal, RAW — the caller's mutation `.error`. Shown above the
   *  fields in the quote surfaces' wording (`errorMessage`), and read for its
   *  per-field codes: `clientContactId: invalid` (a contact that is not one of
   *  the client's, PUT/POST /quotes) lands under the contact picker. The
   *  caller owns the mutation and resets it when it opens the sheet, so the
   *  previous open's refusal never stands here. */
  failure?: unknown
}) {
  const { t } = useTranslation()

  const [draft, setDraft, touched, markTouched] = useFormDraft<QuoteDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? draftFromRecord(record)
        : { ...emptyQuote, quoteDate: today() },
  )

  const clientError =
    touched && !draft.clientId ? t('common.form.required') : undefined

  /* The server's word on the contact, under the contact. `invalid` is the
     ownership check (the contact is not one of the client's AFTER the change,
     or no longer exists): somebody else edited the client's contacts, or the
     client was changed under a contact that stayed — choosing again from the
     list the picker now shows is the fix. Any other code gets its generic
     sentence. */
  const contactCode = fieldErrors(failure)['clientContactId']?.[0]
  const contactError =
    contactCode === undefined
      ? undefined
      : contactCode === 'invalid'
        ? t('quotes.form.clientContactNotOfClient')
        : t(fieldErrorKey(contactCode))

  /* The selected client's own contacts (FR-4, FR-39), from one of two reads.

     While the header still names the QUOTE's own client, through the quote
     (GET /quotes/:id/client-contacts), scoped like the quote itself. Not the
     directory's detail read: that one is scoped to the CLIENT's owner, a
     quote's owner is not always that person (a client reassigned since), and
     it answered them 404 — on a quote they may read and edit, this picker was
     empty and the hint below named nobody. Once another client is picked here,
     from the directory: the picker only lists clients this caller can read.

     Never from the client list this sheet already holds: that list carries no
     contacts at all (directory.ListClients loads none).

     Both reads only while the sheet is OPEN. It is mounted closed on every
     quote page, and useFormDraft seeds the draft's client on mount — so an
     unconditional read fired on pages nobody opened the sheet on.

     Both answer in the directory's order, which is load-bearing:
     quote.firstAddressable picks "the first one carrying an email, primary
     first", and `isDefault` is that pick — served by the quote route,
     restated by toQuoteClientContacts for the directory's rows — so the hint
     cannot name a different person from the one the send would address. */
  const viaQuote = record !== undefined && draft.clientId === record.clientId
  const quoteContacts = useQuoteClientContacts(record?.id ?? '', open && viaQuote)
  const directoryContacts = useClientContacts(
    draft.clientId || undefined,
    open && !viaQuote,
  )
  const contacts: QuoteClientContact[] = viaQuote
    ? (quoteContacts.data ?? [])
    : toQuoteClientContacts(directoryContacts)

  /* The quote read's state from its STATUS, never from the row count
     (failure-modes: a picker's empty state read off `.data`): loading says so
     in the trigger, and a failed read says what failed instead of passing for a
     client with nobody on file. */
  const contactsPending = viaQuote && quoteContacts.isPending
  const contactsError = viaQuote && quoteContacts.isError ? quoteContacts.error : null

  /* Who the offer goes to when no contact is named: the server's own answer,
     never re-derived here, so the hint cannot claim one person and the send
     reach another. */
  const defaultContact = contacts.find((contact) => contact.isDefault)

  /* Every contact is offered, not only the addressable ones. A named contact is
     a deliberate choice the server does not second-guess "even when that contact
     carries no address" (quote.withDefaultRecipient), so hiding the addressless
     ones would remove a choice the domain allows — and the send refuses loudly
     rather than silently mailing somebody else.

     The contact the quote already names stays on offer, by the name the quote
     carries, while the list is not there to show it (loading, or a failed
     read). A picker never drops a stored value it does not recognise: the
     placeholder over a contact that IS set would read as none. */
  const contactOptions = contacts.map((contact) => ({
    value: contact.id,
    label: contact.label,
  }))
  if (
    viaQuote &&
    record?.clientContactName &&
    draft.clientContactId !== '' &&
    draft.clientContactId === record.clientContactId &&
    !contactOptions.some((option) => option.value === draft.clientContactId)
  ) {
    contactOptions.push({
      value: draft.clientContactId,
      label: record.clientContactName,
    })
  }

  const transitInvalid =
    !isBlankOrCount(draft.transitDaysMin) || !isBlankOrCount(draft.transitDaysMax)
  // A minimum above its maximum is not a range. Caught here as well as on the
  // server so the correction happens while the two fields are still on screen.
  const transitInverted =
    draft.transitDaysMin !== '' &&
    draft.transitDaysMax !== '' &&
    Number(draft.transitDaysMin) > Number(draft.transitDaysMax)

  const transitError =
    touched && transitInvalid
      ? t('common.form.invalidNumber')
      : touched && transitInverted
        ? t('quotes.form.transitInverted')
        : undefined

  /* The header measures. Blank is legitimate on all three — it means the sum
     stands — so only a non-numeric entry is refused, which is a typo. */
  const measureInvalid = (raw: string): boolean => !isBlankOrDecimal(raw)
  const grossWeightError =
    touched && measureInvalid(draft.grossWeightKgOverride)
      ? t('common.form.invalidNumber')
      : undefined
  const volumeError =
    touched && measureInvalid(draft.volumeCbmOverride)
      ? t('common.form.invalidNumber')
      : undefined
  const chargeableWeightError =
    touched && measureInvalid(draft.chargeableWeightKg)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    !draft.clientId ||
    transitInvalid ||
    transitInverted ||
    measureInvalid(draft.grossWeightKgOverride) ||
    measureInvalid(draft.volumeCbmOverride) ||
    measureInvalid(draft.chargeableWeightKg) ||
    // S-12: a malformed credit day count, said under its field as it is typed.
    (isCreditTerms(draft.paymentTerms) && !isBlankOrCreditDays(draft.paymentTermsCreditDays))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('quotes.actions.editQuote') : t('quotes.actions.newQuote')}
      submitLabel={record ? undefined : t('quotes.actions.create')}
      pending={pending}
      /* The server's refusal first, as ExpenseFormSheet does: the sheet's own
         check is about what is typed now, and a draft that passes it is the
         one the server just refused. */
      error={
        failure
          ? errorMessage(failure, t)
          : touched && invalid
            ? t('common.form.fixFields')
            : undefined
      }
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('quotes.columns.client')} required error={clientError}>
        {(props) => (
          /* Searchable over name, registry number and code. The list is the
             one this caller may see — Sales sees their own clients, and the
             server is what decides that (AD-5) — so the picker can also create
             one (QW-12), over this sheet rather than instead of it. */
          <ClientSelect
            value={draft.clientId}
            /* The quote's own client name, for a client this caller can no
               longer list (reassigned to another salesperson since): the
               header still says who the offer is for. */
            name={record?.clientName}
            /* The contact is cleared with the client. A contact id belonging to
               the previous client would name a person at a different company —
               and the server would refuse the send rather than notice, so the
               failure would arrive late and read as a directory problem. */
            onChange={(clientId, created) =>
              setDraft({
                ...draft,
                clientId,
                clientContactId: soleContactId(created),
              })
            }
            {...props}
          />
        )}
      </Field>

      {/* FR-4's "customer AND contact" — the person at the client this offer is
          addressed to.
          
          A pick from the selected client's own contacts, never free text: the
          address the offer is emailed to has to be one the directory holds, and
          quote.resolveRecipients refuses any other (a crafted request would
          otherwise email the company's own quoted prices anywhere).
          
          Reset when the client changes, above: a contact belonging to the
          previous client would name a person at a different company.
          
          Blank is the ordinary case and is not a gap. With no contact named the
          server addresses the offer to the client's first contact carrying an
          email, and the detail read shows that same one — one rule, so the
          address SHOWN and the address SENT TO cannot drift. The hint says which
          it will be rather than leaving the reader to guess. */}
      <Field
        label={t('quotes.form.clientContact')}
        error={contactError}
        hint={
          contactsError
            ? t(describeProblem(contactsError).detailKey)
            : draft.clientContactId === '' && defaultContact
              ? t('quotes.form.clientContactDefault', { name: defaultContact.label })
              : t('common.form.optional')
        }
      >
        {(props) => (
          <Select
            value={draft.clientContactId}
            onChange={(clientContactId) => setDraft({ ...draft, clientContactId })}
            options={contactOptions}
            placeholder={
              contactsPending ? t('common.loading') : t('common.form.selectPlaceholder')
            }
            {...props}
          />
        )}
      </Field>

      {/* FR-4's header cargo — whose goods these are and what they are, in one
          line each. NOT the FR-12 positions, which carry their own descriptions
          and are entered on the cargo tab: this is what prints above that table,
          and `cargoOwner` is routinely not the party paying the freight, which
          is why it is its own field rather than the client's name. */}
      <Field label={t('quotes.form.cargoOwner')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.cargoOwner}
            onChange={(e) => setDraft({ ...draft, cargoOwner: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('quotes.form.cargoDescription')}
        hint={t('common.form.optional')}
      >
        {(props) => (
          <Input
            value={draft.cargoDescription}
            onChange={(e) =>
              setDraft({ ...draft, cargoDescription: e.target.value })
            }
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.form.pickupLocation')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.pickupLocation}
            onChange={(e) => setDraft({ ...draft, pickupLocation: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The two dates sit side by side: they are one thought — how long this
          offer stands — and separating them reads as two questions. */}
      <div className="flex gap-2.5">
        <Field label={t('quotes.form.quoteDate')} required className="flex-1">
          {(props) => (
            <DatePicker
              value={draft.quoteDate}
              onChange={(quoteDate) => setDraft({ ...draft, quoteDate })}
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.columns.validUntil')}
          hint={t('quotes.form.validUntilHint')}
          className="flex-1"
        >
          {(props) => (
            <DatePicker
              value={draft.validUntil}
              onChange={(validUntil) => setDraft({ ...draft, validUntil })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* FR-23. Two numbers, not "20-25 days": a text range cannot be compared
          against an actual transit time and cannot survive conversion into the
          case without somebody re-typing it. */}
      <Field
        label={t('quotes.form.transitDays')}
        hint={t('common.form.optional')}
        error={transitError}
      >
        {(props) => (
          <div className="flex items-center gap-2">
            <Input
              inputMode="numeric"
              value={draft.transitDaysMin}
              onChange={(e) => setDraft({ ...draft, transitDaysMin: e.target.value })}
              aria-label={t('quotes.form.transitDaysMin')}
              className="asa-num"
              {...props}
            />
            <span className="text-text-faint shrink-0" aria-hidden>
              –
            </span>
            <Input
              inputMode="numeric"
              value={draft.transitDaysMax}
              onChange={(e) => setDraft({ ...draft, transitDaysMax: e.target.value })}
              aria-label={t('quotes.form.transitDaysMax')}
              className="asa-num"
            />
          </div>
        )}
      </Field>

      <Field label={t('quotes.form.incoterm')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.incoterm}
            onChange={(e) =>
              setDraft({ ...draft, incoterm: e.target.value.toUpperCase() })
            }
            {...props}
          />
        )}
      </Field>

      <Field label={t('quotes.form.paymentTerms')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.paymentTerms}
            onChange={(e) => setDraft({ ...draft, paymentTerms: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* S-12: the credit term's days, under the terms and only while they
          are credit — the same field the deal-terms card reveals, so a credit
          quote edited here is asked the same question and its refusal lands
          under the same words. */}
      {isCreditTerms(draft.paymentTerms) ? (
        <CreditDaysField
          value={draft.paymentTermsCreditDays}
          onChange={(paymentTermsCreditDays) =>
            setDraft({ ...draft, paymentTermsCreditDays })
          }
          serverError={creditDaysError(failure, t)}
        />
      ) : null}

      {/* FR-11's deal terms, in their own fieldset because they are one subject:
          what a delay costs. Both are DISPLAYED on the quote's terms tab and,
          until now, could be filled by nothing — the screen showed "Demurrage —"
          for a value the product had no way to enter.

          `deliveryTerms` is deliberately in here and NOT beside the Incoterm
          above, so the two are not read as one field: the Incoterm is the ICC
          rule that assigns risk and cost (FR-59), and this is our own sentence
          about how delivery actually happens. The hint says so. */}
      <fieldset className="flex flex-col gap-2.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.form.dealTermsLegend')}
        </legend>

        <Field
          label={t('quotes.form.demurrageTerms')}
          hint={t('quotes.form.demurrageTermsHint')}
        >
          {(props) => (
            <Input
              value={draft.demurrageTerms}
              onChange={(e) =>
                setDraft({ ...draft, demurrageTerms: e.target.value })
              }
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.form.deliveryTerms')}
          hint={t('quotes.form.deliveryTermsHint')}
        >
          {(props) => (
            <Input
              value={draft.deliveryTerms}
              onChange={(e) => setDraft({ ...draft, deliveryTerms: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </fieldset>

      {/* The FR-13 header measures. Blank is the normal state and means the
          sum over the positions stands — the hints say so, because a figure
          typed here silently outranks the arithmetic on the offer. */}
      <fieldset className="flex flex-col gap-2.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.form.measures')}
        </legend>

        <div className="flex gap-2.5">
          <Field
            label={t('quotes.form.grossWeightOverride')}
            error={grossWeightError}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.grossWeightKgOverride}
                onChange={(e) =>
                  setDraft({ ...draft, grossWeightKgOverride: e.target.value })
                }
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field
            label={t('quotes.form.volumeOverride')}
            error={volumeError}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.volumeCbmOverride}
                onChange={(e) =>
                  setDraft({ ...draft, volumeCbmOverride: e.target.value })
                }
                className="asa-num"
                {...props}
              />
            )}
          </Field>
        </div>

        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('quotes.form.overrideHint')}
        </p>

        <Field
          label={t('quotes.cargo.chargeableWeight')}
          error={chargeableWeightError}
          hint={t('quotes.form.chargeableWeightHint')}
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.chargeableWeightKg}
              onChange={(e) =>
                setDraft({ ...draft, chargeableWeightKg: e.target.value })
              }
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Checkbox
          label={t('quotes.form.finalPackingList')}
          checked={draft.finalPackingListReceived}
          onCheckedChange={(checked) =>
            setDraft({ ...draft, finalPackingListReceived: checked })
          }
        />
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('quotes.form.finalPackingListHint')}
        </p>
      </fieldset>

      {/* FR-4's optional carrier/supplier DISPLAY line.
          
          LAST, and in its own fieldset with the warning attached, because the
          second clause of Story 2.1's criterion is the load-bearing one: this is
          informational only and assigns NO vendor to any cost line. 0006's column
          comment says the same and
          TestAnonTheCarrierLineIsTextAndNotAParticipantReference pins that it
          never resolves to a participant id.
          
          Free text, therefore, and deliberately not a picker over participants:
          a dropdown of counterparties here would look exactly like the vendor
          field it must not be, and the first person to use it that way would be
          recording an agent on a quote in a column no suppression rule covers
          (FR-29, AD-6). Placed away from the costing fields for the same reason
          — proximity would imply a relationship that does not exist. */}
      <fieldset className="flex flex-col gap-2.5">
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.form.carrierDisplayLegend')}
        </legend>

        <Field
          label={t('quotes.form.carrierDisplay')}
          hint={t('quotes.form.carrierDisplayHint')}
        >
          {(props) => (
            <Input
              value={draft.carrierDisplay}
              onChange={(e) =>
                setDraft({ ...draft, carrierDisplay: e.target.value })
              }
              {...props}
            />
          )}
        </Field>
      </fieldset>

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('quotes.form.nextStep')}
      </p>
    </FormSheet>
  )
}

/** Today, date-only (AD-19). A quote defaults to the day it is written. */
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/* The shared blank, aliased so the call sites below read as they always did.
 * This sheet does not collect participants and no surface does any more: a
 * quote names no external parties (S-22a dropped them, migration 0075). */
const emptyQuote: QuoteDraft = EMPTY_QUOTE_DRAFT
